import { HttpClient, HttpHeaders, HttpParams } from "@angular/common/http";
import { Injectable, inject } from "@angular/core";
import { catchError, finalize, map, of, Subscription, switchMap } from "rxjs";

import { ApplicationConfigurationService } from "@app/core/services/ApplicationConfiguration.service";
import { personFirstNames, personLastName } from "@app/shared/util/person-name";
import { SearchService } from "../components/search-bar/search.service";
import {
  buildInmateSummary,
  GlobalInmateSearchResponse,
  GlobalSearchContext,
  GlobalSearchTrayColumn,
  GlobalSearchTrayPayload,
  GlobalSearchTrayRow,
} from "./global-search.models";

const BASE_PATH = "/api/phms/global-search/inmates";
const MIN_QUERY_LENGTH = 3;
const DEFAULT_LIMIT = 8;

const quietHttpOptions = {
  headers: new HttpHeaders({
    "skip-error-interceptor": "yes",
    "skip-loading-interceptor": "yes",
  }),
  withCredentials: true,
};

const RESULT_COLUMNS: GlobalSearchTrayColumn[] = [
  {
    // Not a row property on purpose: when getValue returns "" the tray falls back to
    // row[name], and an unknown key renders blank instead of leaking the raw display name.
    name: "personLastName",
    displayName: "Last Name",
    type: "string",
    wrapText: "wrapText",
    // Render-time split of the raw display name (#1436); the row keeps the raw value so match
    // highlighting/search behavior is unaffected.
    options: { getValue: (row) => personLastName(row.displayName) },
  },
  {
    name: "personFirstNames",
    displayName: "First Name",
    type: "string",
    wrapText: "wrapText",
    options: { getValue: (row) => personFirstNames(row.displayName) },
  },
  { name: "inmateNumber", displayName: "Inmate #", type: "string" },
  { name: "matchSummary", displayName: "Matched on", type: "string", wrapText: "wrapText" },
  { name: "summary", displayName: "Summary", type: "string", wrapText: "wrapText" },
  {
    name: "detailUrl",
    displayName: "",
    type: "link",
    thAlign: "alignCenter",
    tdAlign: "alignCenter",
    options: {
      text: "Open",
      routerLink: (row: GlobalSearchTrayRow) => ["/case-context/inmate", row.incarceratedPersonId],
    },
  },
];

@Injectable({ providedIn: "root" })
export class GlobalSearchService {
  private readonly http = inject(HttpClient);
  private readonly appConfig = inject(ApplicationConfigurationService);
  private readonly searchBus = inject(SearchService);

  private searchInputSubscription?: Subscription;

  /**
   * Optional per-page override of how results are turned into tray rows/columns. Null = the default
   * "Open" behavior. A feature page sets this on mount and clears it on destroy, so the override is
   * scoped to that page and every other page keeps the default.
   */
  private context: GlobalSearchContext | null = null;

  setContext(context: GlobalSearchContext): void {
    this.context = context;
  }

  /** Clear the context only if it is still the one this caller registered, to avoid clobbering races. */
  clearContext(context: GlobalSearchContext): void {
    if (this.context === context) {
      this.context = null;
    }
  }

  activate(): void {
    if (this.searchInputSubscription) {
      return;
    }

    this.searchInputSubscription = this.searchBus
      .GetSearchInputEmitter()
      .pipe(
        map((value: string) => value.trim()),
        switchMap((query: string) => {
          if (query.length < MIN_QUERY_LENGTH) {
            return of(this.emptyPayload());
          }

          this.searchBus.SetSearchLoading(true);
          return this.searchInmates(query).pipe(
            map((response) => this.buildPayload(response)),
            catchError(() => of(this.emptyPayload())),
            finalize(() => this.searchBus.SetSearchLoading(false)),
          );
        }),
      )
      .subscribe((payload) => {
        this.searchBus.SendSearchResults(payload);
      });
  }

  searchInmates(query: string, limit = DEFAULT_LIMIT) {
    const params = new HttpParams()
      .set("query", query)
      .set("limit", String(limit));

    return this.http.get<GlobalInmateSearchResponse>(
      this.appConfig.apiUrl(BASE_PATH),
      {
        ...quietHttpOptions,
        params,
      },
    );
  }

  /** Delegate to the active page context when one is registered, else the default tray payload. */
  private buildPayload(response: GlobalInmateSearchResponse): GlobalSearchTrayPayload {
    return this.context ? this.context.buildPayload(response) : this.toTrayPayload(response);
  }

  private toTrayPayload(response: GlobalInmateSearchResponse): GlobalSearchTrayPayload {
    const rows = response.results.map((row) => ({
      ...row,
      summary: buildInmateSummary(row),
    }));

    return {
      results: rows,
      model: RESULT_COLUMNS,
      totalCount: response.totalCount,
      recordCount: rows.length,
    };
  }

  private emptyPayload(): GlobalSearchTrayPayload {
    return {
      results: [],
      model: RESULT_COLUMNS,
      totalCount: 0,
      recordCount: 0,
    };
  }
}
